import { Flex } from '@uhg-abyss/web/ui/Flex';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { FacilityDetails } from '../../../models/FacilityDetails';
import { ProviderDetails } from '../../../models/ProviderDetails';
import { useAnalyticsStore } from '../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../store/useAnalyticsStore/analyticsStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { getCurrentMember } from '../../../utils/user.utils';
import { Constants } from '../../Constants';
import { KsAffiliatesBanner } from '../../KsAffliates/KsAffiliatesCard';
import { toShowKSBannerOnProviderDetailsAbout } from '../../KsAffliates/KsAffiliatesUtils';
import { collapseContainer } from '../../ProviderDetailTabs/About/About.styled';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
  convertSpecialtiesList,
  getSearchBlock,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  CollapseProviderDetailsSection,
  ProviderListItem,
} from './CollapseProviderDetailsSection/CollapseProviderDetailsSection';

type Props = {
  providerDetails: FacilityDetails | ProviderDetails;
  tabTitle?: string;
  coverageType: string;
  locationKey?: string;
};

const {
  CULTURAL_COMPLETENCE,
  NATIONAL_PROVIDER_IDENTIFIER,
  PROVIDER_GROUP_ID,
} = Constants.PROVIDER_GROUP_DETAILS.ABOUT_TAB.DETAILS_SECTION;

export const ProviderGroupAbout = ({
  providerDetails,
  tabTitle,
  coverageType,
  locationKey,
}: Props) => {
  const { t } = useTranslation();

  const [
    enableCost,
    enableCulturalCompetency,
    isEnableL2CulturalCompetencies,
    isProviderGroupsDisabled,
    enableIFP,
    enableKelseySeyboldReferralBanner,
    enableAoeSectionForProviderGroups,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CULTURAL_COMPETENCY_TRAINING_SECTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_L2_CULTURAL_COMPETENCIES,
    ConstantsLagoon.FEATURE_FLAGS
      .SUPPRESS_SPECIALTY_SECTION_FOR_PROVIDER_GROUP_BH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_IFP,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_KELSEY_SEYBOLD_REFERRAL_BANNER,
    ConstantsLagoon.FEATURE_FLAGS.PROVIDER_GROUP_DETAILS_AOE_DISPLAY,
  ]);

  const { providerLocations: notAvailableOrUnknownText } =
    useNotAvailableUnknownText();
  const notAvailable = [notAvailableOrUnknownText];
  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );
  const selectedLocationById = providerDetails?.providerLocations?.find(
    (x) => x.locationId === selectedId
  );
  const selectedLocationByKey = providerDetails?.providerLocations?.find(
    (x) => 'locationKey' in x && x.locationKey === locationKey
  );
  const defaultLocation = providerDetails?.providerLocations?.find(
    (x) => x.defaultLocation
  );
  const selectedLocation =
    selectedLocationById ||
    selectedLocationByKey ||
    defaultLocation ||
    providerDetails?.providerLocations?.[0];
  const resolvedProviderGroupId =
    selectedLocation?.pcpId?.replace(' ', '') || providerDetails?.providerId;

  const currentMember = getCurrentMember();

  const allConfigs = useLagoon('config')();
  const ksRegularProviderTins = allConfigs?.find(
    (row) => row.key === 'KELSEY_SEYBOLD_REGULAR_PROVIDERS_TIN'
  )?.value;

  const ksBanner =
    enableKelseySeyboldReferralBanner &&
    enableIFP &&
    toShowKSBannerOnProviderDetailsAbout(
      coverageType,
      providerDetails,
      currentMember,
      ksRegularProviderTins
    );
  const { typeaheadState } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      typeaheadState: state.typeaheadSearchStore,
    }))
  );
  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const getProviderGroupRole = () => {
    let role = 'group';
    if (selectedLocation?.hasPCPServiceAvailability) role += '|pcp';
    return role;
  };

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerDetails.providerType
    )} details`,
    impressionBlock: {
      type: convertProviderTypeToAdobeType(providerDetails.providerType),
      indicator:
        enableCost && selectedLocation?.costIndicator
          ? Constants.ADOBE_TRACKING.COST_ESTIMATE_DISPLAYED
          : '',
    },
    providerBlock: {
      type: convertCoverageType(coverageType),
      specialty:
        convertSpecialtiesList(
          providerDetails?.defaultTranslatedValues?.specialties
        ) || '',
      role: getProviderGroupRole() || '',
    },
    searchBlock: getSearchBlock(typeaheadState, linkName),
  });

  useEffect(() => {
    if (selectedLocation) {
      adobePageTrackEvent();
    }
  }, [selectedLocation !== undefined]);

  const specialities = useMemo(() => {
    if (providerDetails?.specialties?.length)
      return providerDetails.specialties;
    return notAvailable;
  }, [providerDetails?.providerLocations]);

  const areaOfExpertise = useMemo(() => {
    if (providerDetails?.areaOfExpertise?.length)
      return providerDetails.areaOfExpertise;
    return notAvailable;
  }, [providerDetails?.areaOfExpertise]);

  const culturalCompetencyList = useMemo(() => {
    if (providerDetails?.culturalCompetencyCategories?.length)
      return providerDetails.culturalCompetencyCategories;
    return notAvailable;
  }, [providerDetails?.providerLocations]);

  const mappedCultComp = culturalCompetencyList.map(
    (culturalCompetence) => culturalCompetence?.category || notAvailable
  );

  const cultComp = isEnableL2CulturalCompetencies
    ? providerDetails?.culturalCompetencyCategories?.map(
        (i, culturalCompetence) => (
          <React.Fragment key={i}>
            <ul>{`${culturalCompetence?.category}`}</ul>
          </React.Fragment>
        )
      )
    : providerDetails?.culturalCompetencies?.map((i, culturalCompetence) => (
        <React.Fragment key={i}>
          <div>{culturalCompetence}</div>
        </React.Fragment>
      ));

  const providerGroupDetails: Array<ProviderListItem> = useMemo(
    () => [
      ...(!enableCulturalCompetency
        ? [
            {
              title: CULTURAL_COMPLETENCE,
              value: cultComp || notAvailable,
              canCopy: false,
              translation: {
                title: t(
                  'PROVIDER_GROUP_DETAILS.ABOUT_TAB.CULTURAL_COMPETENCE'
                ),
              },
            },
          ]
        : []),
      {
        title: NATIONAL_PROVIDER_IDENTIFIER,
        value: providerDetails?.npi || notAvailable,
        canCopy: Boolean(providerDetails?.npi),
        translation: {
          title: t(
            'PROVIDER_GROUP_DETAILS.ABOUT_TAB.NATIONAL_PROVIDER_IDENTIFIER'
          ),
        },
      },
      {
        title: PROVIDER_GROUP_ID,
        value: resolvedProviderGroupId || notAvailable,
        canCopy: Boolean(resolvedProviderGroupId),
        translation: {
          title: t('PROVIDER_GROUP_DETAILS.ABOUT_TAB.PROVIDER_GROUP_ID'),
        },
      },
    ],
    [providerDetails, resolvedProviderGroupId]
  );

  return (
    <div style={{ overflow: 'auto hidden' }}>
      {ksBanner ? <KsAffiliatesBanner /> : null}
      {enableAoeSectionForProviderGroups && (
        <Flex css={collapseContainer(false)}>
          <CollapseProviderDetailsSection
            accordionItemValue={'areaOfExpertise'}
            areaOfExpertiseDetails={providerDetails?.areaOfExpertiseDetails}
            collapseContent={areaOfExpertise}
            collapseHeader={t(
              'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.AREAS_OF_EXPERTISE'
            )}
            isAreaOfExpertiseSection
          />
        </Flex>
      )}
      <Flex css={collapseContainer(false)}>
        {(!isProviderGroupsDisabled || coverageType != 'B') && (
          <CollapseProviderDetailsSection
            accordionItemValue={'specialities'}
            collapseContent={specialities}
            collapseHeader={t('PROVIDER_GROUP_DETAILS.ABOUT_TAB.SPECIALTIES')}
          />
        )}
        {enableCulturalCompetency && (
          <CollapseProviderDetailsSection
            accordionItemValue={'cultCompetencies'}
            collapseContent={mappedCultComp}
            collapseHeader={t(
              'PROVIDER_GROUP_DETAILS.ABOUT_TAB.CULTURAL_COMPETENCY_TRAINING'
            )}
          />
        )}
      </Flex>
      <Flex css={collapseContainer(false)}>
        <CollapseProviderDetailsSection
          accordionItemValue={'groupDetails'}
          collapseHeader={t(
            'PROVIDER_GROUP_DETAILS.ABOUT_TAB.PROVIDER_GROUP_DETAILS_TITLE'
          )}
          providerGroupDetail={providerGroupDetails}
        />
      </Flex>
    </div>
  );
};
